// SuperAdmin Audit Log Dashboard — the 6 new per-tenant endpoints.
// Platform-DB-backed endpoints (tenant-databases, platform/audit-logs,
// platform/migrations) are already covered by platformService.js — import
// that directly in the tabs that need them, don't duplicate here.

import apiService from "@/lib/api";

const AUDIT_BASE = "/api/v2/audit";

// There is no pagination rewriter here any more, on purpose.
//
// `normalizePagination` used to alias `hasNext`/`hasPrev` onto the snake_case
// keys `DataTableNew` reads. It was written against an assumption that was
// only half true: `PaginationData` is built on the snake_case base, so these
// endpoints wired `has_next`/`has_prev` all along and the aliasing assigned
// `undefined` over the good value. The table kept working only because it
// recomputes the flags when they are missing — the fallback was driving the
// Next button, not the data. The two platform tabs that borrowed the helper
// got the same treatment, and they still speak snake.
//
// `DataTableNew` reads `pg.has_next ?? pg.hasNext ?? …`, so it already knows
// about both casings. One reader that knows, rather than a rewriter in the
// middle that knew about neither.

export const AuditDashboardService = {
  // ------------------------------------------------------------------
  // User Actions tab — per-tenant `audit_logs`
  // ------------------------------------------------------------------

  fetchTenantActions: async ({
    tenantId,
    search = "",
    sort_by = "changed_at",
    sort_order = "desc",
    page = 1,
    per_page = 20,
    pagination = true,
    action,
    entityType,
    entityId,
    actorType,
    changedBy,
    candidateId,
    fromDate,
    toDate,
  } = {}) => {
    if (!tenantId) throw new Error("tenantId is required");
    try {
      const params = {
        tenantId,
        page,
        per_page,
        pagination,
        sortBy: sort_by,
        sortOrder: sort_order,
      };
      if (search) params.search = search;
      if (action) params.action = action;
      if (entityType) params.entityType = entityType;
      if (entityId) params.entityId = entityId;
      if (actorType) params.actorType = actorType;
      if (changedBy) params.changedBy = changedBy;
      if (candidateId) params.candidateId = candidateId;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const response = await apiService.getSecure(`${AUDIT_BASE}/tenant-actions`, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching tenant actions:", error);
      throw error;
    }
  },

  fetchTenantActionDetail: async (auditLogId, tenantId) => {
    try {
      const response = await apiService.getSecure(`${AUDIT_BASE}/tenant-actions/${auditLogId}`, {
        params: { tenantId },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching tenant action detail:", error);
      throw error;
    }
  },

  fetchTenantActionsFilterOptions: async (tenantId) => {
    try {
      const response = await apiService.getSecure(`${AUDIT_BASE}/tenant-actions/filter-options`, {
        params: { tenantId },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching tenant actions filter options:", error);
      throw error;
    }
  },

  // ------------------------------------------------------------------
  // LLM Calls tab — per-tenant `llm_call_audits`
  // ------------------------------------------------------------------

  fetchTenantLlmCalls: async ({
    tenantId,
    search = "",
    sort_by = "created_at",
    sort_order = "desc",
    page = 1,
    per_page = 20,
    pagination = true,
    feature,
    modelName,
    runId,
    entityType,
    entityId,
    fromDate,
    toDate,
  } = {}) => {
    if (!tenantId) throw new Error("tenantId is required");
    try {
      const params = {
        tenantId,
        page,
        per_page,
        pagination,
        sortBy: sort_by,
        sortOrder: sort_order,
      };
      if (search) params.search = search;
      if (feature) params.feature = feature;
      if (modelName) params.modelName = modelName;
      if (runId) params.runId = runId;
      if (entityType) params.entityType = entityType;
      if (entityId) params.entityId = entityId;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const response = await apiService.getSecure(`${AUDIT_BASE}/tenant-llm-calls`, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching tenant LLM calls:", error);
      throw error;
    }
  },

  fetchTenantLlmCallDetail: async (llmCallId, tenantId) => {
    try {
      const response = await apiService.getSecure(`${AUDIT_BASE}/tenant-llm-calls/${llmCallId}`, {
        params: { tenantId },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching tenant LLM call detail:", error);
      throw error;
    }
  },

  fetchTenantLlmCallsFilterOptions: async (tenantId) => {
    try {
      const response = await apiService.getSecure(`${AUDIT_BASE}/tenant-llm-calls/filter-options`, {
        params: { tenantId },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching tenant LLM calls filter options:", error);
      throw error;
    }
  },
};
